import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  listCloudViews,
  saveCloudView,
  updateCloudView,
  deleteCloudView,
} from '../lib/cloudFiles';
import FeedbackButton from './FeedbackButton';

// Custom filter function for multi-select value filtering and text search
const multiSelectFilter = (row, columnId, filterValue) => {
  if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true;
  const cellValue = String(row.getValue(columnId) ?? '');
  // String search (from column-specific search)
  if (typeof filterValue === 'string') {
    return cellValue.toLowerCase().includes(filterValue.toLowerCase());
  }
  // Array filter (from multi-select dropdowns)
  return filterValue.includes(cellValue);
};

const FREE_VIEW_LIMIT = 3;

function SpreadsheetViewer({ data, fileName, sheets, activeSheetIndex, onSheetChange, onNewFile, onGoHome, onLogout, cloudFiles, activeCloudFileId, onSaveToCloud, onOpenCloudFile, onDeleteCloudFile, cloudLoading, user, profile, isPremium }) {
  const isCloudFile = !!activeCloudFileId;
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const filterRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  const [searchColumn, setSearchColumn] = useState('__all__');
  const [headerColor, setHeaderColor] = useState('slate');
  const [fontFamily, setFontFamily] = useState('inter');
  const [showPagination, setShowPagination] = useState(true);
  const [savedPageSize, setSavedPageSize] = useState(10);
  const [columnVisibility, setColumnVisibility] = useState(
    data.columns.reduce((acc, col) => ({ ...acc, [col]: true }), {})
  );
  const [columnSizingInfo, setColumnSizingInfo] = useState({});
  const [columnSizing, setColumnSizing] = useState({});
  const [savedViews, setSavedViews] = useState([]);
  const [newViewName, setNewViewName] = useState('');
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showSheets, setShowSheets] = useState(false);
  const [activeViewId, setActiveViewId] = useState(null);
  const fullscreenRef = useRef(null);

  // Track whether the active view has unsaved changes
  const activeView = useMemo(() => {
    return activeViewId ? savedViews.find(v => v.id === activeViewId) ?? null : null;
  }, [activeViewId, savedViews]);

  const viewHasChanges = useMemo(() => {
    if (!activeView) return false;
    const eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    return (
      activeView.headerColor !== headerColor ||
      activeView.fontFamily !== fontFamily ||
      activeView.showPagination !== showPagination ||
      !eq(activeView.columnVisibility, columnVisibility) ||
      !eq(activeView.columnSizing, columnSizing) ||
      !eq(activeView.columnFilters || [], columnFilters)
    );
  }, [activeView, headerColor, fontFamily, showPagination, columnVisibility, columnSizing, columnFilters]);

  // Load saved views: from Supabase for cloud files, localStorage for local files
  useEffect(() => {
    if (isCloudFile) {
      listCloudViews(activeCloudFileId)
        .then(views => setSavedViews(views.map(v => ({ id: v.id, name: v.name, ...v.settings }))))
        .catch(err => console.error('Failed to load cloud views:', err));
    } else {
      const saved = localStorage.getItem(`quicksheet_views_${fileName}`);
      if (saved) {
        try {
          setSavedViews(JSON.parse(saved));
        } catch (err) {
          console.error('Failed to load saved views:', err);
        }
      } else {
        setSavedViews([]);
      }
    }
  }, [fileName, isCloudFile, activeCloudFileId]);

  // Save views to localStorage only for local files
  useEffect(() => {
    if (!isCloudFile) {
      localStorage.setItem(`quicksheet_views_${fileName}`, JSON.stringify(savedViews));
    }
  }, [savedViews, fileName, isCloudFile]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await fullscreenRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  // Reset state when switching sheets
  useEffect(() => {
    setGlobalFilter('');
    setColumnFilters([]);
    setSearchColumn('__all__');
    setColumnVisibility(
      data.columns.reduce((acc, col) => ({ ...acc, [col]: true }), {})
    );
    setSelectedRowData(null);
    setSelectedRowId(null);
    setOpenFilter(null);
    setFilterSearch('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheetIndex]);

  // Close filter dropdown on click outside
  useEffect(() => {
    if (!openFilter) return;
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setOpenFilter(null);
        setFilterSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilter]);

  // Get unique values for a column (from all rows, not just filtered)
  const getUniqueValues = useCallback((columnId) => {
    const values = new Set();
    data.rows.forEach(row => {
      values.add(String(row[columnId] ?? ''));
    });
    return Array.from(values).sort();
  }, [data.rows]);

  // Get the current selected values for a column filter
  const getSelectedValues = useCallback((columnId) => {
    const filter = columnFilters.find(f => f.id === columnId);
    return filter ? filter.value : null;
  }, [columnFilters]);

  // Toggle a single value in a column's filter
  const toggleFilterValue = useCallback((columnId, value) => {
    setColumnFilters(prev => {
      const existing = prev.find(f => f.id === columnId);
      const allValues = getUniqueValues(columnId);

      if (!existing) {
        // First time filtering: select all except this value
        const newValues = allValues.filter(v => v !== value);
        return [...prev, { id: columnId, value: newValues }];
      }

      const currentValues = existing.value;
      const hasValue = currentValues.includes(value);

      let newValues;
      if (hasValue) {
        newValues = currentValues.filter(v => v !== value);
      } else {
        newValues = [...currentValues, value];
      }

      // If all values selected, remove the filter entirely
      if (newValues.length === allValues.length) {
        return prev.filter(f => f.id !== columnId);
      }

      // If no values selected, keep empty array (shows nothing)
      return prev.map(f => f.id === columnId ? { ...f, value: newValues } : f);
    });
  }, [getUniqueValues]);

  // Select all / deselect all for a column
  const setAllFilterValues = useCallback((columnId, selectAll) => {
    setColumnFilters(prev => {
      if (selectAll) {
        return prev.filter(f => f.id !== columnId);
      } else {
        return [
          ...prev.filter(f => f.id !== columnId),
          { id: columnId, value: [] },
        ];
      }
    });
  }, []);

  // Clear a single column's filter
  const clearColumnFilter = useCallback((columnId) => {
    setColumnFilters(prev => prev.filter(f => f.id !== columnId));
  }, []);

  const headerColorClasses = {
    copper: 'bg-copper-500 hover:bg-copper-600',
    navy: 'bg-navy-900 hover:bg-navy-800',
    slate: 'bg-slate-600 hover:bg-slate-700',
    stone: 'bg-stone-600 hover:bg-stone-700',
    emerald: 'bg-emerald-700 hover:bg-emerald-800',
    rose: 'bg-rose-600 hover:bg-rose-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
    teal: 'bg-teal-600 hover:bg-teal-700',
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    zinc: 'bg-zinc-700 hover:bg-zinc-800',
    violet: 'bg-violet-600 hover:bg-violet-700',
    cyan: 'bg-cyan-700 hover:bg-cyan-800',
  };

  const fontClasses = {
    inter: 'font-sans',
    helvetica: 'font-helvetica',
    mono: 'font-mono',
    serif: 'font-serif',
    georgia: 'font-georgia',
    courier: 'font-courier',
    garamond: 'font-garamond',
    palatino: 'font-palatino',
    trebuchet: 'font-trebuchet',
    verdana: 'font-verdana',
    tahoma: 'font-tahoma',
    lucida: 'font-lucida',
    futura: 'font-futura',
    optima: 'font-optima',
    didot: 'font-didot',
    avenir: 'font-avenir',
  };

  const saveCurrentView = async (viewName) => {
    if (!viewName.trim()) return;

    const trimmedName = viewName.trim();

    // Check if a view with this name already exists
    const existingView = savedViews.find(v => v.name === trimmedName);

    // Enforce free tier limit for new views
    if (!existingView && !isPremium && savedViews.length >= FREE_VIEW_LIMIT) {
      return;
    }

    const settings = {
      headerColor,
      fontFamily,
      showPagination,
      columnVisibility,
      columnSizing,
      columnFilters,
    };

    if (existingView) {
      // Update the existing view
      if (isCloudFile && user) {
        try {
          await updateCloudView(existingView.id, settings);
          setSavedViews(prev => prev.map(v => v.id === existingView.id ? { ...v, ...settings } : v));
        } catch (err) {
          console.error('Failed to update cloud view:', err);
        }
      } else {
        setSavedViews(prev => prev.map(v => v.id === existingView.id ? { ...v, ...settings } : v));
      }
      setActiveViewId(existingView.id);
    } else {
      // Create a new view
      if (isCloudFile && user) {
        try {
          const saved = await saveCloudView(activeCloudFileId, user.id, trimmedName, settings);
          setSavedViews(prev => [...prev, { id: saved.id, name: saved.name, ...saved.settings }]);
          setActiveViewId(saved.id);
        } catch (err) {
          console.error('Failed to save cloud view:', err);
        }
      } else {
        const newView = { id: Date.now(), name: trimmedName, ...settings };
        setSavedViews(prev => [...prev, newView]);
        setActiveViewId(newView.id);
      }
    }

    setNewViewName('');
  };

  const loadView = (view) => {
    setActiveViewId(view.id);
    setHeaderColor(view.headerColor);
    setFontFamily(view.fontFamily);
    setShowPagination(view.showPagination);
    setColumnVisibility(view.columnVisibility || data.columns.reduce((acc, col) => ({ ...acc, [col]: true }), {}));
    setColumnSizing(view.columnSizing || {});
    setColumnFilters(view.columnFilters || []);
  };

  const deleteView = async (viewId) => {
    if (isCloudFile) {
      try {
        await deleteCloudView(viewId);
      } catch (err) {
        console.error('Failed to delete cloud view:', err);
      }
    }
    setSavedViews(prev => prev.filter(v => v.id !== viewId));
  };

  const columns = useMemo(
    () => data.columns.map(columnName => ({
      accessorKey: columnName,
      header: columnName,
      cell: info => info.getValue(),
      size: 150,
      enableColumnResizing: true,
      filterFn: multiSelectFilter,
    })),
    [data.columns]
  );

  const table = useReactTable({
    data: data.rows,
    columns,
    state: {
      globalFilter,
      columnFilters,
      columnVisibility,
      columnSizingInfo,
      columnSizing,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingInfoChange: setColumnSizingInfo,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: 'onEnd',
  });

  // When pagination is off, show all rows; when back on, restore saved page size
  useEffect(() => {
    if (!showPagination) {
      setSavedPageSize(table.getState().pagination.pageSize);
      table.setPageSize(data.rows.length || 9999);
    } else {
      table.setPageSize(savedPageSize);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPagination]);

  const handlePrint = () => {
    const visibleColumns = data.columns.filter(col => columnVisibility[col] !== false);
    const rows = table.getFilteredRowModel().rows;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const sheetName = sheets && sheets.length > 1 ? ` - ${sheets[activeSheetIndex].name}` : '';

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${fileName}${sheetName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; padding: 20px; color: #1e1e2e; }
    .header { margin-bottom: 16px; }
    .header h1 { font-family: Georgia, serif; font-size: 18px; font-weight: 600; }
    .header p { font-size: 12px; color: #6b7280; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { background: #2a2840; color: white; text-align: left; padding: 6px 10px; font-weight: 600; font-size: 10px; }
    td { padding: 5px 10px; border-bottom: 1px solid #ede0ce; }
    tr:nth-child(even) { background: #fdfbf7; }
    @media print {
      body { padding: 0; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${fileName}${sheetName}</h1>
    <p>${rows.length} rows &middot; ${visibleColumns.length} columns</p>
  </div>
  <table>
    <thead><tr>${visibleColumns.map(col => `<th>${col}</th>`).join('')}</tr></thead>
    <tbody>${rows.map(row =>
      `<tr>${visibleColumns.map(col => {
        const val = row.getValue(col);
        return `<td>${val != null ? String(val).replace(/</g, '&lt;').replace(/>/g, '&gt;') : ''}</td>`;
      }).join('')}</tr>`
    ).join('')}</tbody>
  </table>
</body>
</html>`);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleExportCSV = () => {
    const visibleColumns = data.columns.filter(col => columnVisibility[col] !== false);
    const csv = [
      visibleColumns.join(','),
      ...table.getFilteredRowModel().rows.map(row =>
        visibleColumns.map(col => {
          const value = row.getValue(col);
          // Escape quotes and wrap in quotes if contains comma
          const escaped = String(value).replace(/"/g, '""');
          return escaped.includes(',') ? `"${escaped}"` : escaped;
        }).join(',')
      )
    ].join('\n');

    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv));
    element.setAttribute('download', `${fileName.replace(/\.[^.]+$/, '')}_exported.csv`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div ref={fullscreenRef} className="flex h-full bg-cream-50">
      {/* Sidebar */}
      {!isFullscreen && sidebarOpen && (
        <div className="w-64 bg-cream-100 border-r border-cream-300 flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-cream-300">
            <button onClick={onGoHome} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/assets/logo_2.png" alt="QuickSheet" className="w-6 h-6 object-contain" />
              <span className="font-semibold text-navy-900 text-sm">QuickSheet</span>
            </button>
          </div>

          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {/* Files Section */}
            <div className="mb-6">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 mb-2">
                Local Files
              </div>
              <div className="space-y-1">
                {/* Open File Button */}
                <button
                  onClick={onNewFile}
                  className="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-cream-200 rounded transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                  </svg>
                  Open File
                </button>

                {/* Current File */}
                <div className="px-2 py-1.5 bg-cream-200 rounded">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm text-navy-900 font-medium truncate" title={fileName}>
                      {fileName}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 ml-6">Default View</div>
                </div>
              </div>
            </div>

            {/* Cloud Files Section */}
            {user && (
              <div className="mb-6">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 mb-2">
                  Cloud Files
                </div>
                {cloudLoading && (
                  <div className="px-2 py-2 flex items-center gap-2 text-xs text-gray-400">
                    <div className="animate-spin h-3 w-3 border border-copper-500 border-t-transparent rounded-full"></div>
                    Loading...
                  </div>
                )}
                {!cloudLoading && cloudFiles && cloudFiles.length === 0 && (
                  <div className="px-2 py-2 text-xs text-gray-400 italic">
                    No cloud files yet
                  </div>
                )}
                <div className="space-y-1">
                  {cloudFiles && cloudFiles.map(cf => (
                    <div key={cf.id} className={`group flex items-center justify-between px-2 py-1.5 rounded transition-colors ${
                      activeCloudFileId === cf.id ? 'bg-cream-200' : 'hover:bg-cream-200'
                    }`}>
                      <button
                        onClick={() => onOpenCloudFile(cf)}
                        className="flex-1 text-left text-sm text-gray-700 flex items-center gap-2 truncate"
                      >
                        <svg className={`w-4 h-4 flex-shrink-0 ${activeCloudFileId === cf.id ? 'text-copper-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3-3m0 0l3 3m-3-3v12" />
                        </svg>
                        <span className={`truncate ${activeCloudFileId === cf.id ? 'text-navy-900 font-medium' : ''}`}>{cf.file_name}</span>
                      </button>
                      <button
                        onClick={() => onDeleteCloudFile(cf.id, cf.storage_path)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-600 transition-all flex-shrink-0"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                {/* Save to Cloud button - only when viewing a local file */}
                {!isCloudFile && data && user && (
                  <button
                    onClick={onSaveToCloud}
                    disabled={cloudLoading}
                    className="w-full mt-2 text-left px-2 py-1.5 text-sm text-copper-600 hover:bg-cream-200 rounded transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    {cloudLoading ? 'Saving...' : 'Save Current to Cloud'}
                  </button>
                )}
              </div>
            )}

            {/* Sheets Section */}
            {sheets && sheets.length > 1 && (
              <div className="mb-6">
                <button
                  onClick={() => setShowSheets(!showSheets)}
                  className="w-full flex items-center justify-between px-2 mb-2"
                >
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Sheets ({sheets.length})
                  </span>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${showSheets ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showSheets && (
                  <div className="space-y-1">
                    {sheets.map((sheet, index) => (
                      <button
                        key={index}
                        onClick={() => onSheetChange(index)}
                        className={`w-full text-left px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-2 ${
                          index === activeSheetIndex
                            ? 'bg-cream-200 text-navy-900 font-medium'
                            : 'text-gray-700 hover:bg-cream-200'
                        }`}
                      >
                        <svg className={`w-4 h-4 flex-shrink-0 ${index === activeSheetIndex ? 'text-copper-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 18h18M3 6h18" />
                        </svg>
                        <span className="truncate">{sheet.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Views Section */}
            <div className="mb-6">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 mb-2">
                Views
              </div>
              <div className="space-y-1">
                {savedViews.length > 0 ? (
                  savedViews.map(view => (
                    <div key={view.id} className={`group flex items-center justify-between px-2 py-1.5 rounded transition-colors ${
                      activeViewId === view.id ? 'bg-cream-200' : 'hover:bg-cream-200'
                    }`}>
                      <button
                        onClick={() => loadView(view)}
                        className="flex-1 text-left text-sm flex items-center gap-2 truncate"
                      >
                        <svg className={`w-4 h-4 flex-shrink-0 ${activeViewId === view.id ? 'text-copper-500' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span className={`truncate ${activeViewId === view.id ? 'text-navy-900 font-medium' : 'text-gray-700'}`}>{view.name}</span>
                      </button>
                      <button
                        onClick={() => deleteView(view.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-600 transition-all flex-shrink-0"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="px-2 py-2 text-xs text-gray-400 italic">
                    No saved views yet
                  </div>
                )}
              </div>
            </div>

            {/* Customize Section */}
            <div className="border-t border-cream-300 pt-3">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-cream-200 rounded transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                  View Settings
                </div>
                <svg className={`w-4 h-4 transition-transform ${showSettings ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Settings Panel in Sidebar */}
              {showSettings && (
                <div className="mt-2 p-3 bg-cream-50 border border-cream-300 rounded space-y-4">
                  {/* Header Color */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Header Color</label>
                    <div className="flex gap-2 flex-wrap">
                      {Object.keys(headerColorClasses).map(color => (
                        <button
                          key={color}
                          onClick={() => setHeaderColor(color)}
                          className={`w-6 h-6 rounded transition-all border-2 ${
                            headerColor === color ? 'border-navy-900 scale-110' : 'border-cream-300'
                          } ${headerColorClasses[color]}`}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Font */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Font</label>
                    <select
                      value={fontFamily}
                      onChange={(e) => setFontFamily(e.target.value)}
                      className="w-full px-2 py-1.5 border border-cream-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-copper-500 focus:border-copper-500"
                    >
                      <option value="inter">Sans Serif</option>
                      <option value="helvetica">Helvetica</option>
                      <option value="avenir">Avenir</option>
                      <option value="futura">Futura</option>
                      <option value="trebuchet">Trebuchet</option>
                      <option value="verdana">Verdana</option>
                      <option value="tahoma">Tahoma</option>
                      <option value="optima">Optima</option>
                      <option value="serif">Serif</option>
                      <option value="georgia">Georgia</option>
                      <option value="garamond">Garamond</option>
                      <option value="palatino">Palatino</option>
                      <option value="didot">Didot</option>
                      <option value="mono">Monospace</option>
                      <option value="courier">Courier</option>
                      <option value="lucida">Lucida Console</option>
                    </select>
                  </div>

                  {/* Pagination Toggle */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Pagination</label>
                    <button
                      onClick={() => setShowPagination(!showPagination)}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                        showPagination ? 'bg-copper-500' : 'bg-cream-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                          showPagination ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Column Visibility */}
                  <div className="pt-3 border-t border-cream-300">
                    <label className="block text-xs font-medium text-gray-700 mb-2">Columns</label>
                    <input
                      type="text"
                      value={columnSearch}
                      onChange={(e) => setColumnSearch(e.target.value)}
                      placeholder="Search columns..."
                      className="w-full px-2 py-1.5 border border-cream-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-copper-500 focus:border-copper-500 mb-2"
                    />
                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                      {data.columns.filter(col => col.toLowerCase().includes(columnSearch.toLowerCase())).map((column) => (
                        <button
                          key={column}
                          onClick={() =>
                            setColumnVisibility(prev => ({
                              ...prev,
                              [column]: !prev[column],
                            }))
                          }
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                            columnVisibility[column] ?? true
                              ? 'bg-navy-900 text-white'
                              : 'bg-cream-200 text-gray-500'
                          }`}
                        >
                          {column}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Save View */}
                  <div className="pt-3 border-t border-cream-300">
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      {activeViewId ? 'Save or Create View' : 'Save Current View'}
                    </label>
                    {/* Update active view button */}
                    {activeView && (
                      <button
                        onClick={() => saveCurrentView(activeView.name)}
                        disabled={!viewHasChanges}
                        className={`w-full mb-2 px-3 py-1.5 text-white text-xs font-medium rounded transition-colors ${
                          viewHasChanges ? 'bg-copper-500 hover:bg-copper-600' : 'bg-cream-300 cursor-not-allowed'
                        }`}
                      >
                        Update "{activeView.name}"
                      </button>
                    )}
                    {(() => {
                      const isNewView = !newViewName.trim() || !savedViews.some(v => v.name === newViewName.trim());
                      const atLimit = !isPremium && savedViews.length >= FREE_VIEW_LIMIT && isNewView;
                      return (
                        <>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newViewName}
                              onChange={(e) => setNewViewName(e.target.value)}
                              placeholder="View name..."
                              disabled={atLimit && !newViewName.trim()}
                              className="flex-1 px-2 py-1.5 border border-cream-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-copper-500 focus:border-copper-500 disabled:opacity-50"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  saveCurrentView(newViewName);
                                }
                              }}
                            />
                            <button
                              onClick={() => saveCurrentView(newViewName)}
                              disabled={atLimit}
                              className={`px-3 py-1.5 text-white text-xs font-medium rounded transition-colors ${
                                atLimit ? 'bg-cream-300 cursor-not-allowed' : 'bg-navy-900 hover:bg-navy-800'
                              }`}
                            >
                              Save
                            </button>
                          </div>
                          {newViewName.trim() && savedViews.some(v => v.name === newViewName.trim()) && (
                            <p className="text-xs text-copper-600 mt-1">This will update the existing "{newViewName.trim()}" view</p>
                          )}
                          {!isPremium && savedViews.length >= FREE_VIEW_LIMIT && (
                            <p className="text-xs text-gray-500 mt-1">
                              {FREE_VIEW_LIMIT}/{FREE_VIEW_LIMIT} views used. <span className="text-copper-500 font-medium">Upgrade to Premium</span> for unlimited views.
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-cream-300 space-y-1">
            {/* User Info */}
            {profile && (
              <div className="flex items-center gap-2.5 px-2 py-2 mb-1">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt=""
                    className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-copper-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-semibold">
                      {(profile.full_name || profile.email || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  {profile.full_name && (
                    <div className="text-sm font-medium text-navy-900 truncate">{profile.full_name}</div>
                  )}
                  <div className="text-xs text-gray-500 truncate">{profile.email}</div>
                </div>
              </div>
            )}

            {/* Collapse Sidebar Button */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="w-full text-left px-2 py-1.5 text-sm text-gray-600 hover:bg-cream-200 rounded transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
              Collapse
            </button>

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="w-full text-left px-2 py-1.5 text-sm text-gray-600 hover:bg-cream-200 rounded transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col overflow-hidden ${fontClasses[fontFamily]}`}>
        {/* Compact Toolbar */}
        {!isFullscreen && (
          <div className="flex items-center gap-3 px-4 py-3 border-b border-cream-300 bg-cream-100">
            {/* Left Side - Toggle Sidebar + Filename */}
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-1.5 text-gray-500 hover:bg-cream-200 rounded transition-colors"
                title="Show sidebar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            {/* Search Input with Column Selector */}
            <div className="relative flex items-center flex-1 max-w-md border border-cream-300 rounded-lg bg-white overflow-hidden focus-within:ring-1 focus-within:ring-copper-500 focus-within:border-copper-500">
              <select
                value={searchColumn}
                onChange={e => {
                  const newCol = e.target.value;
                  // Clear previous search when switching
                  if (searchColumn === '__all__') {
                    setGlobalFilter('');
                  } else {
                    table.getColumn(searchColumn)?.setFilterValue(undefined);
                  }
                  setSearchColumn(newCol);
                }}
                className="w-25 flex-shrink-0 pl-2 pr-1 py-2 text-xs text-gray-500 bg-cream-100 border-r border-cream-300 focus:outline-none cursor-pointer truncate"
              >
                <option value="__all__">All</option>
                {data.columns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
              <svg className="ml-2 w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={searchColumn === '__all__' ? 'Search all columns...' : `Search ${searchColumn}...`}
                value={searchColumn === '__all__' ? globalFilter : (table.getColumn(searchColumn)?.getFilterValue() ?? '')}
                onChange={e => {
                  if (searchColumn === '__all__') {
                    setGlobalFilter(e.target.value);
                  } else {
                    table.getColumn(searchColumn)?.setFilterValue(e.target.value || undefined);
                  }
                }}
                className="flex-1 pl-2 pr-3 py-2 text-sm bg-transparent focus:outline-none"
              />
            </div>

            {/* Filter summary badge */}
            {columnFilters.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-copper-500/10 text-copper-600 text-xs font-medium rounded-full">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {columnFilters.length} filter{columnFilters.length > 1 ? 's' : ''}
              </span>
            )}

            {/* Filename */}
            <span className="text-sm font-medium text-navy-900 truncate hidden sm:inline">{fileName}</span>

            {/* Update View button in toolbar */}
            {activeView && viewHasChanges && (
              <button
                onClick={() => saveCurrentView(activeView.name)}
                className="px-3 py-1.5 text-xs font-medium text-white bg-copper-500 hover:bg-copper-600 rounded-lg transition-colors"
              >
                Update "{activeView.name}"
              </button>
            )}

            {/* Right Side - Actions */}
            <div className="flex items-center gap-1 ml-auto">
              <button
                onClick={handleExportCSV}
                className="px-3 py-1.5 text-sm text-gray-700 hover:bg-cream-200 rounded transition-colors"
              >
                Export
              </button>
              <button
                onClick={handlePrint}
                className="px-3 py-1.5 text-sm text-gray-700 hover:bg-cream-200 rounded transition-colors"
              >
                Print
              </button>
              <button
                onClick={toggleFullscreen}
                className="px-3 py-1.5 text-sm text-gray-700 hover:bg-cream-200 rounded transition-colors"
                title={isFullscreen ? "Exit Presentation" : "Present"}
              >
                {isFullscreen ? 'Exit' : 'Present'}
              </button>
              <div className="w-px h-5 bg-cream-300 mx-1" />
              <FeedbackButton user={user} />
            </div>
          </div>
        )}

        {/* Table Content */}
        <div className="flex-1 overflow-auto p-4">
          <div className="bg-cream-50 border border-cream-300 rounded-xl shadow-lg overflow-hidden flex flex-col h-full">
            <div className="relative flex-1 overflow-auto">
              <div className="min-w-full">
                <table
                  className="w-full border-collapse"
                  style={{
                    tableLayout: 'fixed',
                  }}
                >
                  <thead className="sticky top-0 z-10">
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id} className="border-b border-cream-300">
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            className={`px-4 py-2.5 text-left text-xs font-semibold text-white relative group ${headerColorClasses[headerColor]}`}
                            style={{
                              width: header.getSize() > 0 ? header.getSize() : `calc(100% / ${headerGroup.headers.length})`,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}
                          >
                            <div className="flex items-center gap-1">
                              <span className="flex-1">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setColumnVisibility(prev => ({
                                    ...prev,
                                    [header.column.id]: false,
                                  }));
                                }}
                                className="p-0.5 rounded hover:bg-white/20 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-70"
                                title={`Hide ${header.column.id}`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenFilter(openFilter === header.column.id ? null : header.column.id);
                                  setFilterSearch('');
                                }}
                                className={`p-0.5 rounded hover:bg-white/20 transition-colors flex-shrink-0 ${
                                  getSelectedValues(header.column.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-70'
                                }`}
                                title="Filter column"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                </svg>
                              </button>
                            </div>
                            {/* Filter Dropdown */}
                            {openFilter === header.column.id && (
                              <div
                                ref={filterRef}
                                className="absolute top-full left-0 mt-1 w-56 bg-white border border-cream-300 rounded-lg shadow-lg z-50 text-gray-700 font-normal"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {/* Search input */}
                                <div className="p-2 border-b border-cream-200">
                                  <input
                                    type="text"
                                    placeholder="Search values..."
                                    value={filterSearch}
                                    onChange={(e) => setFilterSearch(e.target.value)}
                                    className="w-full px-2 py-1.5 border border-cream-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-copper-500 focus:border-copper-500"
                                    autoFocus
                                  />
                                </div>
                                {/* Select All / Clear */}
                                <div className="px-2 py-1.5 border-b border-cream-200 flex items-center justify-between">
                                  <button
                                    onClick={() => setAllFilterValues(header.column.id, true)}
                                    className="text-xs text-copper-500 hover:text-copper-600"
                                  >
                                    Select All
                                  </button>
                                  <button
                                    onClick={() => setAllFilterValues(header.column.id, false)}
                                    className="text-xs text-copper-500 hover:text-copper-600"
                                  >
                                    Clear
                                  </button>
                                </div>
                                {/* Value checkboxes */}
                                <div className="max-h-48 overflow-y-auto p-1">
                                  {(() => {
                                    const allValues = getUniqueValues(header.column.id);
                                    const selected = getSelectedValues(header.column.id);
                                    const filtered = filterSearch
                                      ? allValues.filter(v => v.toLowerCase().includes(filterSearch.toLowerCase()))
                                      : allValues;

                                    return filtered.map((value) => {
                                      const isChecked = selected === null || selected.includes(value);
                                      return (
                                        <label
                                          key={value}
                                          className="flex items-center gap-2 px-2 py-1 hover:bg-cream-100 rounded cursor-pointer text-xs"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleFilterValue(header.column.id, value)}
                                            className="rounded border-cream-300 text-copper-500 focus:ring-copper-500 h-3.5 w-3.5"
                                          />
                                          <span className="truncate">{value || '(empty)'}</span>
                                        </label>
                                      );
                                    });
                                  })()}
                                </div>
                                {/* Apply / Clear filter button */}
                                {getSelectedValues(header.column.id) && (
                                  <div className="p-2 border-t border-cream-200">
                                    <button
                                      onClick={() => {
                                        clearColumnFilter(header.column.id);
                                        setOpenFilter(null);
                                        setFilterSearch('');
                                      }}
                                      className="w-full px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                                    >
                                      Remove Filter
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              className="absolute right-0 top-0 h-full w-1 select-none touch-none bg-white/20 hover:bg-white/40 cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{
                                transform: columnSizingInfo.isResizingColumn === header.id
                                  ? `translateX(${columnSizingInfo.deltaOffset}px)`
                                  : '',
                              }}
                            />
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row, idx) => (
                      <tr
                        key={row.id}
                        className={`border-b border-cream-200 transition-colors cursor-pointer ${
                          selectedRowId === row.id
                            ? 'bg-copper-500/10'
                            : `hover:bg-cream-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-cream-50'}`
                        }`}
                        onDoubleClick={() => {
                          const rowData = {};
                          data.columns.forEach(col => {
                            rowData[col] = row.getValue(col);
                          });
                          setSelectedRowData(rowData);
                          setSelectedRowId(row.id);
                        }}
                      >
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            className="px-4 py-2.5 text-sm text-gray-800"
                            style={{
                              width: cell.column.getSize(),
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {table.getRowModel().rows.length === 0 && (
                  <div className="flex items-center justify-center h-48 bg-cream-50">
                    <div className="text-center">
                      <p className="text-gray-500">No results found</p>
                      <p className="text-gray-400 text-sm mt-1">Try adjusting your search</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Detail Panel */}
              {selectedRowData && (
                <div className="fixed right-0 top-0 bottom-0 w-80 bg-cream-50 border-l border-cream-300 overflow-y-auto shadow-lg z-30">
                  {/* Header */}
                  <div className="sticky top-0 bg-cream-50 p-4 flex items-center justify-between border-b border-cream-300">
                    <h3 className="text-sm font-semibold text-navy-900">Row Details</h3>
                    <button
                      onClick={() => { setSelectedRowData(null); setSelectedRowId(null); }}
                      className="p-1 hover:bg-cream-200 rounded transition-colors text-gray-500"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-5">
                    {data.columns.map((column) => (
                      <div key={column}>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                          {column}
                        </label>
                        <div className="text-sm text-navy-900 leading-relaxed">
                          {selectedRowData[column] || '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Pagination */}
            {showPagination && (
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-cream-300 bg-cream-100 text-sm text-gray-500">
                <span>Showing {table.getFilteredRowModel().rows.length} of {data.rows.length} rows</span>
                <div className="flex items-center gap-3">
                  <select
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value));
                    }}
                    className="px-2 py-1 border border-cream-300 rounded text-sm text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-copper-500 focus:border-copper-500"
                  >
                    {[10, 20, 30, 40, 50].map(pageSize => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize} rows
                      </option>
                    ))}
                  </select>

                  <div className="flex gap-1">
                    <button
                      onClick={() => table.setPageIndex(0)}
                      disabled={!table.getCanPreviousPage()}
                      className="p-1.5 border border-cream-300 rounded hover:bg-cream-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-gray-600"
                      title="First page"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L10.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L4.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                      className="p-1.5 border border-cream-300 rounded hover:bg-cream-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-gray-600"
                      title="Previous page"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                      className="p-1.5 border border-cream-300 rounded hover:bg-cream-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-gray-600"
                      title="Next page"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                      disabled={!table.getCanNextPage()}
                      className="p-1.5 border border-cream-300 rounded hover:bg-cream-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-gray-600"
                      title="Last page"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L10.586 10 6.293 5.707a1 1 0 010-1.414zm6 0a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L14.586 10l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SpreadsheetViewer;
